/**
 * useStaffing - React hooks for the staffing API (Needs, Candidates, Interviews).
 */
import { useState, useEffect, useCallback } from "react";
import apiClient from "@/lib/api";

// ─── Needs ─────────────────────────────────────────────────────────────────

export function useNeeds() {
  const [needs, setNeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNeeds = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/recruitment/needs");
      setNeeds(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load needs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNeeds(); }, [fetchNeeds]);

  const createNeed = useCallback(async (data) => {
    const res = await apiClient.post("/recruitment/needs", data);
    await fetchNeeds();
    return res.data;
  }, [fetchNeeds]);

  const updateNeed = useCallback(async (id, data) => {
    const res = await apiClient.put(`/recruitment/needs/${id}`, data);
    await fetchNeeds();
    return res.data;
  }, [fetchNeeds]);

  const deleteNeed = useCallback(async (id) => {
    await apiClient.delete(`/recruitment/needs/${id}`);
    await fetchNeeds();
  }, [fetchNeeds]);

  return { needs, loading, error, fetchNeeds, createNeed, updateNeed, deleteNeed };
}

export function useNeed(needId) {
  const [need, setNeed] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!needId) return;
    setLoading(true);
    Promise.all([
      apiClient.get(`/recruitment/needs/${needId}`),
      apiClient.get(`/recruitment/needs/${needId}/candidates`),
    ]).then(([needRes, candidatesRes]) => {
      setNeed(needRes.data);
      setCandidates(candidatesRes.data);
    }).catch((err) => {
      setError(err?.response?.data?.detail || "Failed to load need");
    }).finally(() => setLoading(false));
  }, [needId]);

  return { need, candidates, loading, error };
}

export function useNeedMatches(needId) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMatches = useCallback(async () => {
    if (!needId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/recruitment/needs/${needId}/matches`);
      setMatches(res.data.items || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec du chargement des correspondances.");
    } finally {
      setLoading(false);
    }
  }, [needId]);

  useEffect(() => {
    void fetchMatches();
  }, [fetchMatches]);

  return { matches, loading, error, refetch: fetchMatches };
}

// ─── Candidates ────────────────────────────────────────────────────────────

export function useCandidates() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/recruitment/candidates");
      setCandidates(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const createCandidate = useCallback(async (data) => {
    const res = await apiClient.post("/recruitment/candidates", data);
    await fetchCandidates();
    return res.data;
  }, [fetchCandidates]);

  const updateCandidate = useCallback(async (id, data) => {
    const res = await apiClient.put(`/recruitment/candidates/${id}`, data);
    await fetchCandidates();
    return res.data;
  }, [fetchCandidates]);

  return { candidates, loading, error, fetchCandidates, createCandidate, updateCandidate };
}

export function useCandidate(candidateId) {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCandidate = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/recruitment/candidates/${candidateId}`);
      setCandidate(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load candidate");
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { fetchCandidate(); }, [fetchCandidate]);

  const moveStage = useCallback(async (candidateNeedId, stage, notes, rejectionReason) => {
    const res = await apiClient.post(`/recruitment/candidates/${candidateId}/move-stage`, {
      candidate_need_id: candidateNeedId,
      stage,
      notes,
      rejection_reason: rejectionReason,
    });
    await fetchCandidate();
    return res.data;
  }, [candidateId, fetchCandidate]);

  const linkNeed = useCallback(async (needId, notes) => {
    const res = await apiClient.post(`/recruitment/candidates/${candidateId}/link-need`, {
      need_id: needId,
      notes,
    });
    await fetchCandidate();
    return res.data;
  }, [candidateId, fetchCandidate]);

  const sendCvReview = useCallback(async () => {
    const res = await apiClient.post(`/recruitment/candidates/${candidateId}/send-cv-review`, {});
    return res.data;
  }, [candidateId]);

  const createCollab = useCallback(async () => {
    const res = await apiClient.post(`/recruitment/candidates/${candidateId}/create-collab`, {});
    return res.data;
  }, [candidateId]);

  return { candidate, loading, error, fetchCandidate, moveStage, linkNeed, sendCvReview, createCollab };
}

export function useCandidateMatches(candidateId) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!candidateId) return;
    setLoading(true);
    apiClient.get(`/recruitment/candidates/${candidateId}/matches`)
      .then((res) => setMatches(res.data.items || []))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load matches"))
      .finally(() => setLoading(false));
  }, [candidateId]);

  return { matches, loading, error };
}

// ─── Interviews ────────────────────────────────────────────────────────────

export function useInterviews(filters = {}) {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.candidateId) params.candidate_id = filters.candidateId;
      if (filters.needId) params.need_id = filters.needId;
      if (filters.candidateNeedId) params.candidate_need_id = filters.candidateNeedId;
      const res = await apiClient.get("/recruitment/interviews", { params });
      setInterviews(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load interviews");
    } finally {
      setLoading(false);
    }
  }, [filters.candidateId, filters.needId, filters.candidateNeedId]);

  useEffect(() => { fetchInterviews(); }, [fetchInterviews]);

  const createInterview = useCallback(async (data) => {
    const res = await apiClient.post("/recruitment/interviews", data);
    await fetchInterviews();
    return res.data;
  }, [fetchInterviews]);

  const updateInterview = useCallback(async (id, data) => {
    const res = await apiClient.put(`/recruitment/interviews/${id}`, data);
    await fetchInterviews();
    return res.data;
  }, [fetchInterviews]);

  const deleteInterview = useCallback(async (id) => {
    await apiClient.delete(`/recruitment/interviews/${id}`);
    await fetchInterviews();
  }, [fetchInterviews]);

  return { interviews, loading, error, fetchInterviews, createInterview, updateInterview, deleteInterview };
}
